<script setup lang="ts" generic="TCommand extends string | number = string">
import { ElDropdown } from 'element-plus'

/**
 * A menu of commands: the trigger in the default slot, a `DropdownMenu` of `DropdownItem`s
 * in `#dropdown`, and the picked command on `@command`.
 *
 * **Pass `commands`** — the list of command strings this menu can emit. It renders nothing;
 * it is what makes the component generic over them, so `@command` arrives typed and the
 * handler needs no `$event as …` cast back. The items live in slot content, so a prop is
 * the only place the type can come from. Left off, the emit is a bare `string`.
 *
 * A table row's actions are `TableActions`, which is this already assembled.
 */

const props = withDefaults(
  defineProps<{
    trigger?: 'hover' | 'click' | 'contextmenu'
    placement?:
      | 'top'
      | 'top-start'
      | 'top-end'
      | 'bottom'
      | 'bottom-start'
      | 'bottom-end'
    /**
     * Close the menu when an item is picked. Left unset element-plus closes it, which is
     * what a menu of commands wants; pass `false` for one whose items are toggles.
     */
    hideOnClick?: boolean
    disabled?: boolean
    splitButton?: boolean
    size?: 'large' | 'default' | 'small'
    showTimeout?: number
    hideTimeout?: number
    commands?: readonly TCommand[]
  }>(),
  {
    // Spelled out because Vue casts an absent boolean prop to `false` only when it has no
    // default at all: without this line every dropdown hands element-plus
    // `:hide-on-click="false"`, and a menu stays open — on the row's *old* actions — after
    // its command has already run.
    hideOnClick: undefined
  }
)

const emit = defineEmits<{
  (e: 'command', command: TCommand): void
  (e: 'click'): void
  (e: 'visible-change', visible: boolean): void
}>()
</script>

<template>
  <ElDropdown
    :trigger="trigger"
    :placement="placement"
    :hide-on-click="props.hideOnClick"
    :disabled="disabled"
    :split-button="splitButton"
    :size="size"
    :show-timeout="showTimeout"
    :hide-timeout="hideTimeout"
    v-bind="$attrs"
    @command="emit('command', $event as TCommand)"
    @click="emit('click')"
    @visible-change="emit('visible-change', $event)"
  >
    <slot />
    <template #dropdown>
      <slot name="dropdown" />
    </template>
  </ElDropdown>
</template>
